<template>
    <v-container :class="{'pa-0' : $vuetify.breakpoint.xsOnly}" id="body-wrapper">
        <v-layout row wrap>
            <v-flex mt-4 xs12 text-center>
                <v-form
                    v-model="isCancelFormValid"
                    ref="formCancel"
                    @submit.prevent="submitCancel">
                    <v-card raised>
                        <v-card-title v-if="hasGroupUuid">
                            {{ $t(isMultiAppt ? 'cancel.cancelMultipleTitle' : 'cancel.cancelSingleTitle') }}
                        </v-card-title>
                        <v-card-title
                            v-else
                            tabindex="0"
                            aria-labelledby="cancelTitle"
                        >
                            <p id="cancelTitle">
                                {{ $t('cancel.cancelTitle') }}
                            </p>
                        </v-card-title>
                        <v-card-text>
                            <v-text-field
                                v-if="!hasGroupUuid && !groupUuidFromEmail"
                                v-model="noCoupon"
                                :label="$t('cancel.couponNumber')"
                                outlined
                                append-icon="fas fa-ticket-alt"
                                :aria-label="noCoupon && noCoupon===routerQueryCouponNo ? $t('cancel.accessibility.prefilledCouponNumber') :$t('cancel.couponNumber')"
                                :rules="[
                                    v => !!v || $t('cancel.couponNumberRequired'),
                                    v => (v ? /^[a-z|A-Z|0-9]{5,6}$/.test(v) : true) || $t('cancel.errors.invalidCouponNumber')
                                ]"
                            />
                            <v-text-field
                                v-if="!hasGroupUuid && groupUuidFromEmail"
                                v-model="groupUuidFromEmail"
                                :label="$t('cancel.groupUuidFromEmail')"
                                outlined
                                append-icon="fas fa-ticket-alt"
                                :rules="[
                                    v => !!v || $t('cancel.couponNumberRequired'),
                                ]"
                            />
                            <v-text-field
                                v-if="!hasGroupUuid && !groupUuidFromEmail"
                                v-model="apptDate"
                                :label="$t('cancel.appointmentDate')"
                                :placeholder="$i18n.locale === 'fr' ? 'AAAA-MM-JJ' : 'YYYY-MM-DD'"
                                :aria-label="apptDate && apptDate.replace(/-/g,'') === routerQueryApptDate ? $t('cancel.accessibility.prefilledAppointmentDate') :$t('cancel.appointmentDate')"
                                outlined
                                :rules="[
                                    v => !!v || $t('cancel.appointmentDateRequired'),
                                    v => !v || getFormRule('futureDate', 'cancel.errors.appointmentDateInvalid')(v),
                                ]"
                                v-mask="$t('base.dateMask')"
                            />
                            <TrimozRecaptcha
                                v-if="!isIpWhitelisted && showCouponTrimozRecaptcha"
                                ref="cancelRecaptcha"
                                id="recaptcha-div"
                                @onCaptchaAcquired="validateCancelApptRecaptcha($event)"
                                @onCaptchaExpired="onCaptchaExpired"
                            />
                            <template v-if="hasGroupUuid">
                                <v-layout class="pb-2">
                                    <v-spacer />
                                    <v-flex xs12 md6>
                                        <v-layout column>
                                            <v-flex xs12 text-left>
                                                <span class="coupon-label font-weight-regular">
                                                    {{ $t('coupon.service') }}
                                                </span>
                                                <span>{{ serviceName }}</span>
                                            </v-flex>
                                            <v-flex text-left>
                                                <span class="coupon-label">
                                                    {{ $t('coupon.date') }}
                                                </span>
                                                <span>{{ appointment.date }}</span>
                                            </v-flex>
                                            <v-flex text-left>
                                                <span class="coupon-label">
                                                    {{ $t('coupon.hour') }}
                                                </span>
                                                <span>{{ appointment.hour }} ({{ serviceLength }} min)</span>
                                            </v-flex>
                                            <v-flex text-left>
                                                <span class="coupon-label">
                                                    {{ $t('coupon.place') }}
                                                </span>
                                                <span>{{ placeName }}</span>
                                            </v-flex>
                                            <v-flex text-left>
                                                <span class="coupon-label">
                                                    {{ $t('coupon.address') }}
                                                </span>
                                                <span>{{ placeAddress }}</span>
                                            </v-flex>
                                        </v-layout>
                                    </v-flex>
                                    <v-spacer />
                                </v-layout>
                                <v-layout v-if="Object.keys(groupAppointmentNumbers).length > 1" class="pb-2">
                                    <v-spacer />
                                    <v-flex xs12 md6>
                                        <v-list class="elevation-1">
                                            <v-list-tile
                                                v-for="(value, apptNumber) in groupAppointmentNumbers"
                                                :key="apptNumber"
                                            >
                                                <v-list-item-action>
                                                    <v-checkbox
                                                        v-model="groupAppointmentNumbers[apptNumber]"
                                                        class="mt-0 py-0 pa-2"
                                                        color="primary"
                                                        on-icon="check_box"
                                                        off-icon="check_box_outline_blank"
                                                    />
                                                </v-list-item-action>
                                                <v-list-item-content
                                                    @click="groupAppointmentNumbers[apptNumber] = !groupAppointmentNumbers[apptNumber]"
                                                >
                                                    <v-list-item-title v-text="apptNumber" />
                                                    <v-divider aria-hidden="true" />
                                                </v-list-item-content>
                                            </v-list-tile>
                                        </v-list>
                                    </v-flex>
                                    <v-spacer />
                                </v-layout>
                            </template>
                            <v-flex xs12 text-center>
                                <v-btn
                                    class="button-secondary-color rounded-pill"
                                    :disabled="isCancelBtnDisabled"
                                    :aria-label="groupUuid ? accessibilityApptToBeCancelled: ''"
                                    id="cancel-button"
                                    @click="onCancelSubmit(groupUuid)"
                                >
                                    {{ $t(Object.keys(groupAppointmentNumbers).length > 1 ? 'cancel.cancelMyAppointments' : 'cancel.cancelMyAppointment') }}
                                </v-btn>
                                <v-btn
                                    v-if="hasGroupUuid"
                                    @click="removeGroupCancelForm()"
                                >
                                    {{ $t(Object.keys(groupAppointmentNumbers).length > 1 ? 'cancel.keepMyAppointments' : 'cancel.keepMyAppointment') }}
                                </v-btn>
                            </v-flex>
                        </v-card-text>
                    </v-card>
                </v-form>
            </v-flex>
            <v-flex mt-4 xs12 text-center>
                <v-form
                    v-model="isCouponLostFormValid"
                    ref="formCouponLost"
                    @submit.prevent="submitCouponLost"
                >
                    <v-card
                        raised
                        tabindex="0"
                        aria-labelledby="couponLostTitle"
                    >
                        <v-card-title id="couponLostTitle">
                            {{ $t('cancel.couponLostTitle') }}
                        </v-card-title>
                        <v-card-text>
                            <v-text-field
                                v-model="email"
                                :label="$t('form.email')"
                                outlined
                                append-icon="fas fa-ticket-alt"
                                :rules="[
                                    v => !!v || $t('form.errors.email'),
                                    v => !v || getFormRule('email')(v)
                                ]"
                            />
                            <v-flex xs12 text-center>
                                <TrimozRecaptcha
                                    v-if="!isIpWhitelisted && isEmailFormatValid"
                                    ref="couponLostRecaptcha"
                                    id="recaptcha-div-email"
                                    @onCaptchaAcquired="validateCouponLostRecaptcha($event)"
                                    @onCaptchaExpired="onCaptchaExpired"
                                />
                            </v-flex>
                            <v-flex xs12 text-center>
                                <v-btn
                                    class="button-secondary-color rounded-pill text-wrap d-inline-block"
                                    :disabled="isCouponLostBtnDisabled"
                                    @click="couponLostAPI"
                                >
                                    {{ $t('cancel.getMyAppointment') }}
                                </v-btn>
                            </v-flex>
                        </v-card-text>
                    </v-card>
                </v-form>
            </v-flex>
        </v-layout>

        <v-layout my-12 row wrap>
            <v-flex xs12 sm10 offset-sm1 text-center>
                <div
                    tabindex="0"
                    aria-labelledby="unableToCancel"
                >
                    <p id="unableToCancel">
                        {{ $t('cancel.unableToCancel') }}
                    </p>
                </div>
            </v-flex>
        </v-layout>

        <v-divider aria-hidden="true" />
    </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { size, reduce, toUpper, isNil, some, split } from 'lodash';
import { formatDatefromJsDate, formatTimefromJsDate, stringDate, setFocusForAccessibility } from '@/utils';
import { differenceInMinutes } from 'date-fns';
import { TrimozError } from '@trimoz/trimoz-vue-error-handling';
import { formRules } from '@/mixins/form-rules';
import TrimozRecaptcha from '@/components/TrimozRecaptcha';

export default {
    name: 'cancel-appt',
    components: { TrimozRecaptcha },
    mixins: [formRules],
    data() {
        return {
            // Cancellation form
            noCoupon: null,
            apptDate: null,
            groupUuid: null,
            groupAppointmentNumbers: {},
            fetchingAppointmentGroup: false,
            cancellingAppointments: false,
            appointment: {},

            // Validation
            isCancelFormValid: false,
            isCancelRecaptchaValid: false,
            isCancelRecaptchaAlreadyValidated: false,

            // Coupon lost form
            email: '',
            isCouponLostFormValid: false,
            isCouponLostRecaptchaValid: false,

            // if multi appointments
            isMultiAppt: false,

            // recaptcha token
            recaptchaToken: '',

            // group uuid from email set in the url
            groupUuidFromEmail: null,
        };
    },
    async mounted() {
        // If the route has a query param appointmentNumber
        if (this.routerQueryCouponNo) {
            // Set it as coupon #
            this.noCoupon = this.routerQueryCouponNo;
        }

        // If the route has a query param appointmentDate
        if (this.routerQueryApptDate) {
            // Set it as appointment date
            this.apptDate = this.routerQueryApptDate;
        }

        // If the route has a query param group
        if (this.$route.query.group) {
            // Set it as appt group uuid
            this.groupUuidFromEmail = this.$route.query.group;
        }
    },
    created() {
        this.setApiSecurityWhitelistingIp();
    },
    watch: {
        /**
         * reset recaptcha on lg change to render translated component
         * */
        locale(newLocale, oldLocale) {
            if (!newLocale || !oldLocale) return;
            if (newLocale !== oldLocale) {
                this.resetCaptcha();
            }
        },
    },
    computed: {
        ...mapGetters({
            captchaKey: 'captchaKey',
            locale: 'locale',
            upperLocale: 'upperLocale',
            getEstablishment: 'establishment/getEstablishment',
            isIpWhitelisted: 'isIpWhitelisted',
        }),

        /**
         * Verify if the "cancel my appointment" button is disabled or not
         */
        isCancelBtnDisabled() {
            return (this.groupUuid
                ? (!this.groupApptNumbersChecked || (!this.isIpWhitelisted && !this.isCancelRecaptchaValid))
                : (!this.isIpWhitelisted && !this.isCancelRecaptchaValid) || !this.isCancelFormValid);
        },

        isCouponLostBtnDisabled() {
            return (!this.isIpWhitelisted && !this.isCouponLostRecaptchaValid) || !this.isCouponLostFormValid;
        },

        /**
         * Verify if email format is valid before showing recaptcha
         */
        isEmailFormatValid() {
            return this.getFormRule('email')(this.email) === true;
        },

        /**
         * Returns whether there are checked appt numbers in the list
         */
        groupApptNumbersChecked() {
            return this.canOverrideSubmitValidation || some(this.groupAppointmentNumbers, Boolean);
        },

        canOverrideSubmitValidation() {
            return Object.keys(this.groupAppointmentNumbers).length <= 1 && this.groupUuidFromEmail;
        },

        serviceName() {
            return this.appointment.establishmentServiceTemplate[`name${this.upperLocale}`];
        },

        serviceLength() {
            return differenceInMinutes(this.appointment.stop, this.appointment.start);
        },

        placeName() {
            return this.appointment.place[`name${this.upperLocale}`];
        },

        placeAddress() {
            return this.appointment.place.formattedAddress;
        },

        hasGroupUuid() {
            return !isNil(this.groupUuid);
        },

        showCouponTrimozRecaptcha() {
            if (!this.hasGroupUuid && this.groupUuidFromEmail) {
                return true;
            }
            if (!this.hasGroupUuid && (/^[a-z|A-Z|0-9]{5,6}$/.test(this.noCoupon))) {
                if (/^20[23][0-9]-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/.test(this.apptDate)) {
                    return true;
                }
            }
            return false;
        },
        accessibilityApptToBeCancelled() {
            return this.$t('cancel.accessibility.apptToBeCancelled', [this.appointment.date, this.appointment.hour, this.serviceName]);
        },

        routerQueryCouponNo() {
            return this.$route.query.appointmentNumber;
        },

        routerQueryApptDate() {
            return this.$route.query.appointmentDate;
        },

    },
    methods: {
        ...mapActions({
            fetchGroupOfAppointment: 'appointments/fetchGroupOfAppointment',
            cancelAppointment: 'other/cancelAppointment',
            sendCouponLostMail: 'other/sendCouponLostMail',
            setApiSecurityWhitelistingIp: 'setApiSecurityWhitelistingIp',
        }),

        /**
         * Dispatch to the another function based on if groupUuid is set
         */
        async onCancelSubmit(groupUuid) {
            if(groupUuid) {
                await this.cancelAppointmentsAPI(this.apptDate);
            }
            else {
                setFocusForAccessibility('cancel-button', 1500);
                await this.fetchAppointmentGroupAPI(this.noCoupon, this.apptDate);
            }
        },
        /**
         * Reset recaptcha when expired
         */
        onCaptchaExpired() {
            if (this.$refs.cancelRecaptcha) {
                this.isCancelRecaptchaValid = false;
                this.$refs.cancelRecaptcha.reset();
            }
            if (this.$refs.couponLostRecaptcha) {
                this.isCouponLostRecaptchaValid = false;
                this.$refs.couponLostRecaptcha.reset();
            }
        },

        /**
         * Calls the API route multiple times to cancel each
         * appointments (Could be improved with batch call)
         */
        async cancelAppointmentsAPI(apptDate) {
            this.cancellingAppointments = true;
            try {
                // loop throughout groupAppointmentNumbers and delete checked appointment number
                for (let apptNumber in this.groupAppointmentNumbers) {
                    if (this.groupAppointmentNumbers[apptNumber] || this.canOverrideSubmitValidation) {
                        await this.cancelAppointment({
                            appointmentNumber: apptNumber,
                            establishmentId: this.getEstablishment.id,
                            recaptchaToken: this.isIpWhitelisted ? '' : this.recaptchaToken,
                            date: apptDate,
                        });
                    }
                }

                this.cancellingAppointments = false;
                this.$store.commit('snackbar/SET_SNACK', { message: this.$i18n.t('cancel.cancelSuccess'), color: 'success', timeout: 5000 });
            }
            finally {
                this.resetFormCancel();
                this.resetCaptcha();
                this.removeGroupCancelForm();
            }
        },

        resetFormCancel() {
            if (this.$refs.formCancel) {
                this.$refs.formCancel.reset();
            }
        },

        resetFormCouponLost() {
            if (this.$refs.formCouponLost) {
                this.$refs.formCouponLost.reset();
            }
        },

        resetCaptcha() {
            // Reset the captcha on action
            if (this.$refs.cancelRecaptcha) {
                this.$refs.cancelRecaptcha.reset();
            }
            if (this.$refs.couponLostRecaptcha) {
                this.$refs.couponLostRecaptcha.reset();
            }
            this.isCancelRecaptchaValid = false;
            this.isCouponLostRecaptchaValid = false;
        },

        /**
         * Calls the API route to send an email with a list of appointment
         * associated to provided email
         */
        async couponLostAPI() {
            try {
                let response = await this.sendCouponLostMail({
                    username: this.email,
                    locale: this.locale + '-ca',
                    recaptchaToken: this.isIpWhitelisted ? '' : this.recaptchaToken,
                });

                if (response.status === 204) {
                    this.$store.commit('snackbar/SET_SNACK', { message: this.$i18n.t('cancel.couponLostSuccessNoContent'), color: 'warning', timeout: 5000 });
                }
                else {
                    this.$store.commit('snackbar/SET_SNACK', { message: this.$i18n.t('cancel.couponLostSuccess'), color: 'success', timeout: 5000 });
                }

                this.email = '';
                this.resetFormCouponLost();
            }
            catch(error) {
                throw new TrimozError(error, 'errors.generic');
            }
            finally {
                this.resetCaptcha();
            }
        },

        /**
         * Fetches an appointment group with either an appt number (@param = false)
         * or an appt group uuid (@param uuid = true)
         * @param apptNumberOrGroupUuid
         * @param apptDate
         * @param uuid
         */
        async fetchAppointmentGroupAPI(apptNumberOrGroupUuid, apptDate, uuid = false) {
            try {
                this.fetchingAppointmentGroup = true;
                if (this.groupUuidFromEmail) {
                    uuid = true;
                    apptNumberOrGroupUuid = this.groupUuidFromEmail;
                }
                const appointments = await this.fetchGroupOfAppointment([apptNumberOrGroupUuid, apptDate, uuid, this.recaptchaToken]);
                this.groupAppointmentNumbers = reduce(appointments, (result, appointment) => {
                    const comparableAppointmentNumber = toUpper(appointment.appointmentNumber);
                    const comparableNoCoupon = toUpper(this.noCoupon);
                    // API/Database lookup is case-insensitive, so the comparison also has to be case insensitive
                    result[appointment.appointmentNumber] = comparableAppointmentNumber === comparableNoCoupon;
                    return result;
                }, {});

                this.groupUuid = appointments[0].groupUuid;
                this.appointment = appointments[0];

                if (size(appointments) > 1) {
                    this.isMultiAppt = true;
                }
                if (!this.apptDate) {
                    // YYYY-MM-DD HH:ii:ss -> YYYYMMDD
                    this.apptDate = split(split(appointments[0].startInTimezone, ' ')[0], '-').join('');
                }

                this.appointment = {
                    ...this.appointment,
                    date: stringDate(formatDatefromJsDate(this.appointment.start), this.locale),
                    hour: formatTimefromJsDate(this.appointment.start),
                };
            }
            catch (error) {
                this.removeGroupCancelForm();
                this.resetFormCancel();

                if (error.httpStatus === 404) {
                    throw new TrimozError(error, 'errors.cancel.couponNotAssociated');
                }

                if (error.httpStatus === 422) {
                    throw new TrimozError(error, 'errors.cancel.couponNotFound');
                }

                throw new TrimozError(error, 'errors.generic');
            }
            finally {
                this.fetchingAppointmentGroup = false;
            }
        },

        /**
         * Removes the form to cancel multiple appointments
         */
        removeGroupCancelForm() {
            this.groupAppointmentNumbers = {};
            this.groupUuid = null;
            this.noCoupon = null;
            this.apptDate = null;
            this.fetchingAppointmentGroup = false;
            this.cancellingAppointments = false;
            this.appointment = {};
        },

        /**
         * Validate recaptcha for delete appt(s)
         */
        validateCancelApptRecaptcha(response) {
            this.isCancelRecaptchaValid = true;
            this.recaptchaToken = response;
        },

        /**
         * Validate recaptcha for lost coupon form
         */
        validateCouponLostRecaptcha(response) {
            this.isCouponLostRecaptchaValid = true;
            this.recaptchaToken = response;
        },

        submitCancel() {
            if (!this.isCancelBtnDisabled) {
                this.onCancelSubmit();
            }
        },

        submitCouponLost() {
            if (!this.isCouponLostBtnDisabled) {
                this.couponLostAPI();
            }
        },
    },
};
</script>
<style lang="scss">
    @import './src/assets/scss/variables';
    .coupon-label {
        color: rgba(0,0,0,.61);
    }
</style>
